import { Component } from "react";
import { AlertCircle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * ErrorBoundary — catches any render-time exception from its subtree and
 * renders a friendly fallback instead of white-screening the whole page.
 *
 * This exists because React, without an ErrorBoundary, unmounts the
 * entire component tree when any child throws during render / lifecycle.
 * The classic trigger is `toast.error(obj)` where `obj` is an object:
 * sonner tries to render the object as a React child, React throws
 * "Objects are not valid as a React child", nothing catches it, the
 * whole page goes blank.
 *
 * Props:
 *   `fallbackTitle`  — short heading on the fallback card.
 *   `fallbackHint`   — one-liner describing what the user can try.
 *   `scope`          — "app" | "subtree"; purely a logging label.
 *   `children`       — the subtree to protect.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Masked — never log the raw error object (may carry PII in message strings).
    // The browser console still shows the full trace from React's own handler.
    const label = this.props?.scope || "subtree";
    // eslint-disable-next-line no-console
    console.error(
      `[ErrorBoundary:${label}] caught`,
      error?.message || String(error),
      info?.componentStack?.split("\n").slice(0, 6).join("\n"),
    );
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
  };

  reload = () => {
    try {
      window.location.reload();
    } catch {
      /* ignore */
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const title = this.props.fallbackTitle || "Something went wrong";
    const hint =
      this.props.fallbackHint ||
      "Please refresh the page. If this keeps happening, contact the admin and share the browser console output.";

    const errorMessage =
      (this.state.error && (this.state.error.message || String(this.state.error))) ||
      "unknown";

    return (
      <div
        role="alert"
        data-testid="error-boundary-fallback"
        className="mx-auto my-10 max-w-xl rounded-lg border border-destructive/40 bg-destructive/5 p-6 text-sm"
      >
        <div className="flex items-start gap-3">
          <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-destructive" aria-hidden="true" />
          <div className="flex-1 space-y-3">
            <div>
              <h2 className="text-base font-semibold text-destructive">{title}</h2>
              <p className="mt-1 text-muted-foreground">{hint}</p>
            </div>
            <details className="text-xs text-muted-foreground">
              <summary className="cursor-pointer select-none">Technical details</summary>
              <pre className="mt-2 overflow-auto rounded bg-background/50 p-2 text-[11px]">
                {String(errorMessage).slice(0, 500)}
              </pre>
            </details>
            <div className="flex gap-2 pt-1">
              <Button size="sm" variant="outline" onClick={this.reset} data-testid="error-boundary-retry">
                <RefreshCw className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                Try again
              </Button>
              <Button size="sm" variant="ghost" onClick={this.reload} data-testid="error-boundary-reload">
                Reload page
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
